/**
 * Onglet "Packs de permissions" — RBAC V2 Self-Service
 * 
 * Permet de :
 * - Lister les 6 packs système + packs custom
 * - Créer un pack custom
 * - Modifier/supprimer
 * - Appliquer un pack à un rôle (union de permissions)
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Package, Plus, Edit, Trash2, Sparkles, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_COLORS = [
  { name: 'Cyan (Finance)', value: '#0891b2' },
  { name: 'Slate (Lecture)', value: '#64748b' },
  { name: 'Violet (Terrain)', value: '#9333ea' },
  { name: 'Orange (Manager)', value: '#ea580c' },
  { name: 'Amber (Commercial)', value: '#ca8a04' },
  { name: 'Ambre (Lead)', value: '#d97706' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Rose', value: '#e11d48' },
];

const PacksManagement = ({ allPermissions = [], allRoles = [], onPackApplied }) => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [targetRoleSlug, setTargetRoleSlug] = useState('');

  const [formData, setFormData] = useState({
    name: '', slug: '', description: '', color: '#0891b2',
    icon: 'package', permissions: [],
  });

  useEffect(() => {
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/rbac/packs`);
      setPacks(res.data || []);
    } catch (err) {
      console.error('Failed to fetch packs:', err);
      toast({ title: 'Erreur', description: 'Impossible de charger les packs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setFormData({
    name: '', slug: '', description: '', color: '#0891b2', icon: 'package', permissions: [],
  });

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Nom requis', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/rbac/packs`, formData);
      toast({ title: 'Pack créé', description: `${formData.name} (${formData.permissions.length} permissions)` });
      setShowCreateDialog(false);
      resetForm();
      fetchPacks();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Échec de création',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async () => {
    if (!selectedPack) return;
    try {
      await axios.put(`${API}/rbac/packs/${selectedPack.id}`, {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        icon: formData.icon,
        permissions: formData.permissions,
      });
      toast({ title: 'Pack mis à jour' });
      setShowEditDialog(false);
      fetchPacks();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Échec de mise à jour',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (pack) => {
    if (!window.confirm(`Supprimer le pack "${pack.name}" ?\n\nCette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/rbac/packs/${pack.id}`);
      toast({ title: 'Pack supprimé' });
      fetchPacks();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Échec de suppression',
        variant: 'destructive',
      });
    }
  };

  const handleApply = async () => {
    if (!selectedPack || !targetRoleSlug) return;
    try {
      const res = await axios.post(`${API}/rbac/roles/${targetRoleSlug}/apply-pack/${selectedPack.id}`);
      toast({
        title: 'Pack appliqué',
        description: `${selectedPack.name} → ${res.data.name} (${res.data.permissions.length} permissions)`,
      });
      setShowApplyDialog(false);
      if (onPackApplied) onPackApplied();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || "Échec de l'application",
        variant: 'destructive',
      });
    }
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId],
    }));
  };

  const openEdit = (pack) => {
    setSelectedPack(pack);
    setFormData({
      name: pack.name,
      slug: pack.slug,
      description: pack.description || '',
      color: pack.color,
      icon: pack.icon,
      permissions: pack.permissions || [],
    });
    setShowEditDialog(true);
  };

  const openApply = (pack) => {
    setSelectedPack(pack);
    setTargetRoleSlug('');
    setShowApplyDialog(true);
  };

  const filteredPacks = packs.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="packs-management-tab">
      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Rechercher un pack..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="packs-search-input"
          />
        </div>
        <Button
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="packs-create-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau pack
        </Button>
      </div>

      <div className="text-sm text-gray-500">
        {loading ? 'Chargement…' : `${packs.length} pack(s) au total · ${packs.filter(p => p.isSystem).length} système · ${packs.filter(p => !p.isSystem).length} custom`}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPacks.map(pack => (
          <Card
            key={pack.id}
            className={`hover:shadow-md transition-shadow ${pack.isSystem ? 'border-l-4' : ''}`}
            style={{ borderLeftColor: pack.isSystem ? pack.color : undefined }}
            data-testid={`pack-card-${pack.slug}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${pack.color}20` }}>
                    <Package className="w-5 h-5" style={{ color: pack.color }} />
                  </div>
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {pack.name}
                      {pack.isSystem && (
                        <Badge variant="outline" className="text-xs">Système</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs font-mono mt-1">
                      {pack.slug}
                    </CardDescription>
                  </div>
                </div>
              </div>
              {pack.description && (
                <p className="text-sm text-gray-600 mt-2">{pack.description}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <Badge variant="secondary">{pack.permissions.length} permissions</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openApply(pack)}
                  className="text-cyan-700 border-cyan-300 hover:bg-cyan-50"
                  data-testid={`pack-apply-${pack.slug}`}
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  Appliquer
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(pack)}
                  data-testid={`pack-edit-${pack.slug}`}
                >
                  <Edit className="w-3.5 h-3.5 mr-1" />
                  Modifier
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(pack)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`pack-delete-${pack.slug}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog: Create / Edit Pack */}
      <Dialog
        open={showCreateDialog || showEditDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setShowEditDialog(false);
            setSelectedPack(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Modifier le pack' : 'Nouveau pack de permissions'}</DialogTitle>
            <DialogDescription>
              Un pack regroupe des permissions thématiques applicables en 1 clic sur n'importe quel rôle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Finance Junior"
                  data-testid="pack-form-name"
                />
              </div>
              <div>
                <Label>Couleur</Label>
                <Select value={formData.color} onValueChange={(v) => setFormData({ ...formData, color: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRESET_COLORS.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <span className="inline-flex items-center gap-2">
                          <span className="w-3 h-3 rounded" style={{ backgroundColor: c.value }} />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="À quoi sert ce pack ?"
                rows={2}
                data-testid="pack-form-description"
              />
            </div>
            <div>
              <Label>Permissions ({formData.permissions.length} sélectionnée(s))</Label>
              <div className="border rounded p-3 max-h-72 overflow-y-auto space-y-2 mt-1">
                {allPermissions.map(perm => (
                  <label
                    key={perm.id}
                    className="flex items-start gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <Checkbox
                      checked={formData.permissions.includes(perm.id)}
                      onCheckedChange={() => togglePermission(perm.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{perm.name}</div>
                      <div className="text-xs text-gray-500 font-mono truncate">{perm.id}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setShowEditDialog(false);
            }}>Annuler</Button>
            <Button
              onClick={showEditDialog ? handleUpdate : handleCreate}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="pack-form-submit"
            >
              {showEditDialog ? 'Mettre à jour' : 'Créer le pack'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Apply pack to role */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Appliquer "{selectedPack?.name}" à un rôle</DialogTitle>
            <DialogDescription>
              Les permissions du pack seront ajoutées au rôle (union, pas de remplacement).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Rôle cible</Label>
            <Select value={targetRoleSlug} onValueChange={setTargetRoleSlug}>
              <SelectTrigger data-testid="pack-apply-role-select">
                <SelectValue placeholder="Sélectionner un rôle..." />
              </SelectTrigger>
              <SelectContent>
                {allRoles.map(r => (
                  <SelectItem key={r.slug} value={r.slug}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: r.color }} />
                      {r.name}
                      <span className="text-xs text-gray-500">({r.permissions?.length || 0} perms)</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyDialog(false)}>Annuler</Button>
            <Button
              onClick={handleApply}
              disabled={!targetRoleSlug}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="pack-apply-submit"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Appliquer le pack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PacksManagement;
